import React, { useState, useRef, useEffect } from 'react';
import { 
  ISO_A_SIZES, 
  ISO_B_SIZES,
  ISO_C_SIZES,
  US_SIZES,
  ANSI_SIZES,
  ARCH_SIZES,
  JIS_SIZES,
  US_ENVELOPE_SIZES,
  PHOTO_SIZES,
  COMMON_REFERENCE_OBJECTS
} from '../data/standardSizes';

// Size Panel component to avoid React hooks violations
const SizePanel = ({
  sizes,
  categoryId,
  categoryName,
  tooltip = '',
  isExpanded,
  togglePanel,
  selectedSizes,
  toggleSize,
  toggleAllInStandard,
  handleAutoZoom,
  getStandardColor,
  compact
}) => {
  const panelRef = useRef(null);
  
  // Save and restore scroll position
  useEffect(() => {
    // If panel is not expanded, nothing to do
    if (!isExpanded || !panelRef.current) return;
    
    // Get stored scroll position from localStorage
    const storedPosition = localStorage.getItem(`scroll-${categoryId}`);
    if (storedPosition) {
      panelRef.current.scrollTop = parseFloat(storedPosition);
    }
  }, [isExpanded, categoryId]);
  
  const handleScroll = () => {
    if (panelRef.current) {
      localStorage.setItem(`scroll-${categoryId}`, panelRef.current.scrollTop.toString());
    }
  };
  
  // Calculate if all items are selected
  const allSelected = sizes.length > 0 && sizes.every(size => selectedSizes[`${categoryId}-${size.id}`]);
  
  // Calculate if some but not all items are selected
  const someSelected = sizes.some(size => selectedSizes[`${categoryId}-${size.id}`]) && !allSelected;
  
  return (
    <div className={`border border-gray-200 rounded mb-2 ${compact ? 'text-sm' : ''}`}>
      <div 
        className={`flex items-center justify-between p-2 cursor-pointer ${getStandardColor(categoryId)}`}
        onClick={() => togglePanel(categoryId)}
      >
        <div className="flex items-center">
          <h3 className="font-medium text-gray-800">{categoryName}</h3>
          {tooltip && (
            <span 
              className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-100 text-gray-600 text-xs cursor-help"
              title={tooltip}
            >
              ⓘ
            </span>
          )}
        </div>
        <span className="text-gray-500">{isExpanded ? '▼' : '▶'}</span>
      </div>
      
      {isExpanded && (
        <div className="p-2 border-t border-gray-200">
          {/* "Select all" option */}
          <div className="flex items-center justify-between mb-1 pb-1 border-b border-gray-100">
            <span className={`text-gray-500 ${compact ? 'text-xs' : 'text-sm'}`}>Select all</span>
            <input 
              type="checkbox" 
              className={`${compact ? 'h-3 w-3' : 'h-4 w-4'}`}
              checked={allSelected}
              ref={el => {
                if (el) {
                  el.indeterminate = someSelected;
                }
              }}
              onChange={(e) => {
                toggleAllInStandard(categoryId, e.target.checked);
              }}
            />
          </div>
          
          <div 
            ref={panelRef}
            className={`space-y-1 ${compact ? 'max-h-40 overflow-y-auto pr-1' : 'space-y-2'}`}
            onScroll={handleScroll}
          >
            {sizes.map(size => {
              const sizeKey = `${categoryId}-${size.id}`;
              
              return (
                <label key={size.id} className="flex items-center justify-between group">
                  <div className="flex items-center">
                    <input 
                      type="checkbox" 
                      className={`mr-2 ${compact ? 'h-3 w-3' : ''}`}
                      checked={selectedSizes[sizeKey] || false}
                      onChange={() => {
                        toggleSize(sizeKey);
                        if (!selectedSizes[sizeKey]) {
                          handleAutoZoom(size);
                        }
                      }}
                    />
                    <span className={`${compact ? 'text-xs' : ''}`}>
                      {size.name} 
                      <span className="text-gray-500 ml-1">
                        ({size.widthDisplay}×{size.heightDisplay})
                      </span>
                    </span>
                  </div>
                  {size.description && (
                    <span 
                      className={`ml-1 ${compact ? 'text-xs' : 'text-xs'} text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity`}
                      title={size.description}
                    >
                      ⓘ
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Reference Objects Panel component
const ReferenceObjectsPanel = ({
  objects,
  isExpanded,
  togglePanel,
  selectedObjects,
  toggleObject,
  toggleAllObjects,
  handleAutoZoom,
  compact
}) => {
  const panelRef = useRef(null);
  
  // Save and restore scroll position
  useEffect(() => {
    // If panel is not expanded, nothing to do
    if (!isExpanded || !panelRef.current) return;
    
    // Get stored scroll position from localStorage
    const storedPosition = localStorage.getItem(`scroll-refobj`);
    if (storedPosition) {
      panelRef.current.scrollTop = parseFloat(storedPosition);
    }
  }, [isExpanded]);
  
  const handleScroll = () => {
    if (panelRef.current) {
      localStorage.setItem(`scroll-refobj`, panelRef.current.scrollTop.toString());
    }
  };
  
  // Calculate if all items are selected
  const allSelected = objects.length > 0 && objects.every(obj => selectedObjects.includes(obj.id));
  
  // Calculate if some but not all items are selected
  const someSelected = objects.some(obj => selectedObjects.includes(obj.id)) && !allSelected;
  
  return (
    <div className={`border border-gray-200 rounded mb-2 ${compact ? 'text-sm' : ''}`}>
      <div 
        className="flex items-center justify-between bg-gray-50 border-l-4 border-gray-400 p-2 cursor-pointer"
        onClick={() => togglePanel('refobj')}
      >
        <div className="flex items-center">
          <h3 className="font-medium text-gray-800">Reference Objects</h3>
        </div>
        <span className="text-gray-500">{isExpanded ? '▼' : '▶'}</span>
      </div>
      
      {isExpanded && (
        <div className="p-2 border-t border-gray-200">
          {/* "Select all" option */}
          <div className="flex items-center justify-between mb-1 pb-1 border-b border-gray-100">
            <span className={`text-gray-500 ${compact ? 'text-xs' : 'text-sm'}`}>Select all</span>
            <input 
              type="checkbox" 
              className={`${compact ? 'h-3 w-3' : 'h-4 w-4'}`}
              checked={allSelected}
              ref={el => {
                if (el) {
                  el.indeterminate = someSelected;
                }
              }}
              onChange={(e) => {
                toggleAllObjects(e.target.checked);
              }}
            />
          </div>
          
          <div 
            ref={panelRef}
            className={`space-y-1 ${compact ? 'max-h-40 overflow-y-auto pr-1' : 'space-y-2'}`}
            onScroll={handleScroll}
          >
            {objects.map(obj => {
              return (
                <label key={obj.id} className="flex items-center justify-between group">
                  <div className="flex items-center">
                    <input 
                      type="checkbox" 
                      className={`mr-2 ${compact ? 'h-3 w-3' : ''}`}
                      checked={selectedObjects.includes(obj.id)}
                      onChange={() => {
                        toggleObject(obj.id);
                      }}
                    />
                    <span className={`${compact ? 'text-xs' : ''}`}>
                      {obj.name} 
                      <span className="text-gray-500 ml-1">
                        ({obj.widthDisplay}×{obj.heightDisplay})
                      </span>
                    </span>
                  </div>
                  {obj.description && (
                    <span 
                      className={`ml-1 ${compact ? 'text-xs' : 'text-xs'} text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity`}
                      title={obj.description}
                    >
                      ⓘ
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export const StandardSizes = ({ 
  panelStates, 
  togglePanel, 
  selectedSizes, 
  toggleSize,
  handleAutoZoom,
  toggleAllInStandard,
  selectedReferenceObjects = [],
  toggleReferenceObject = () => {},
  toggleAllReferenceObjects = () => {},
  compact = false
}) => {
  const containerRef = useRef(null);
  
  // Save scroll position when component unmounts or when selectedSizes changes
  useEffect(() => {
    return () => {
      if (containerRef.current) {
        localStorage.setItem('standard-sizes-scroll', containerRef.current.scrollTop.toString());
      }
    };
  }, [selectedSizes, selectedReferenceObjects]);
  
  // Restore scroll position when component mounts
  useEffect(() => {
    const storedPosition = localStorage.getItem('standard-sizes-scroll');
    if (storedPosition && containerRef.current) {
      containerRef.current.scrollTop = parseFloat(storedPosition);
    }
  }, []);
  
  const handleScroll = () => {
    if (containerRef.current) {
      localStorage.setItem('standard-sizes-scroll', containerRef.current.scrollTop.toString());
    }
  };

  // Function to determine the background color class based on standard category
  const getStandardColor = (categoryId) => {
    switch(categoryId) {
      case 'iso':
      case 'isob':
      case 'isoc':
        return 'bg-blue-50 border-l-4 border-blue-500';
      case 'us':
      case 'usenv':
        return 'bg-amber-50 border-l-4 border-amber-500';
      case 'ansi':
        return 'bg-violet-50 border-l-4 border-violet-600';
      case 'arch':
        return 'bg-orange-50 border-l-4 border-orange-500';
      case 'jis':
        return 'bg-fuchsia-50 border-l-4 border-fuchsia-500';
      case 'photo':
        return 'bg-rose-50 border-l-4 border-rose-500';
      case 'refobj':
        return 'bg-gray-50 border-l-4 border-gray-500';
      default:
        return 'bg-gray-50';
    }
  };
  
  return (
    <div 
      ref={containerRef}
      className={`bg-white rounded-lg shadow-md ${compact ? 'p-3 h-full overflow-y-auto' : 'p-5 mb-6'}`}
      onScroll={handleScroll}
    >
      <div className="flex items-center justify-between mb-3">
        <h2 className={`${compact ? 'text-lg' : 'text-xl'} font-medium text-gray-800`}>Standard Sizes</h2>
        <div className="flex space-x-1">
          <button 
            className={`px-2 ${compact ? 'py-1 text-xs' : 'py-1 text-sm'} bg-gray-100 hover:bg-gray-200 rounded`}
            onClick={() => handleAutoZoom()}
            title="Auto-fit all visible sizes in the view"
          >
            Auto-fit
          </button>
        </div>
      </div>
      
      {/* Size category panels */}
      <SizePanel
        sizes={ISO_A_SIZES}
        categoryId="iso"
        categoryName="ISO A Series"
        tooltip="International standard for paper sizes. A4 is the common office paper size in most countries."
        isExpanded={panelStates.iso}
        togglePanel={togglePanel}
        selectedSizes={selectedSizes}
        toggleSize={toggleSize}
        toggleAllInStandard={toggleAllInStandard}
        handleAutoZoom={handleAutoZoom}
        getStandardColor={getStandardColor}
        compact={compact}
      />
      
      <SizePanel
        sizes={ISO_B_SIZES}
        categoryId="isob"
        categoryName="ISO B Series"
        tooltip="Intermediate sizes between A series. Often used for posters and passports."
        isExpanded={panelStates.isob}
        togglePanel={togglePanel}
        selectedSizes={selectedSizes}
        toggleSize={toggleSize}
        toggleAllInStandard={toggleAllInStandard}
        handleAutoZoom={handleAutoZoom}
        getStandardColor={getStandardColor}
        compact={compact}
      />
      
      <SizePanel
        sizes={ISO_C_SIZES}
        categoryId="isoc"
        categoryName="ISO C Series"
        tooltip="Designed for envelopes. C4 envelope fits A4 paper, C5 fits A5, etc."
        isExpanded={panelStates.isoc}
        togglePanel={togglePanel}
        selectedSizes={selectedSizes}
        toggleSize={toggleSize}
        toggleAllInStandard={toggleAllInStandard}
        handleAutoZoom={handleAutoZoom}
        getStandardColor={getStandardColor}
        compact={compact}
      />
      
      <SizePanel
        sizes={US_SIZES}
        categoryId="us"
        categoryName="US Paper Sizes"
        tooltip='Standard North American paper sizes like Letter (8.5×11") and Legal (8.5×14")'
        isExpanded={panelStates.us}
        togglePanel={togglePanel}
        selectedSizes={selectedSizes}
        toggleSize={toggleSize}
        toggleAllInStandard={toggleAllInStandard}
        handleAutoZoom={handleAutoZoom}
        getStandardColor={getStandardColor}
        compact={compact}
      />
      
      <SizePanel
        sizes={ANSI_SIZES}
        categoryId="ansi"
        categoryName="ANSI Series"
        tooltip="American National Standards Institute sizes for technical drawings."
        isExpanded={panelStates.ansi}
        togglePanel={togglePanel}
        selectedSizes={selectedSizes}
        toggleSize={toggleSize}
        toggleAllInStandard={toggleAllInStandard}
        handleAutoZoom={handleAutoZoom}
        getStandardColor={getStandardColor}
        compact={compact}
      />
      
      <SizePanel
        sizes={ARCH_SIZES}
        categoryId="arch"
        categoryName="ARCH Series"
        tooltip="Architectural drawing series used primarily in the US for construction plans."
        isExpanded={panelStates.arch}
        togglePanel={togglePanel}
        selectedSizes={selectedSizes}
        toggleSize={toggleSize}
        toggleAllInStandard={toggleAllInStandard}
        handleAutoZoom={handleAutoZoom}
        getStandardColor={getStandardColor}
        compact={compact}
      />
      
      <SizePanel
        sizes={JIS_SIZES}
        categoryId="jis"
        categoryName="JIS Series"
        tooltip="Japanese Industrial Standard paper sizes, similar to ISO but with slight differences."
        isExpanded={panelStates.jis}
        togglePanel={togglePanel}
        selectedSizes={selectedSizes}
        toggleSize={toggleSize}
        toggleAllInStandard={toggleAllInStandard}
        handleAutoZoom={handleAutoZoom}
        getStandardColor={getStandardColor}
        compact={compact}
      />
      
      <SizePanel
        sizes={US_ENVELOPE_SIZES}
        categoryId="usenv"
        categoryName="US Envelope Sizes"
        tooltip="Standard envelope sizes used in North America."
        isExpanded={panelStates.usenv}
        togglePanel={togglePanel}
        selectedSizes={selectedSizes}
        toggleSize={toggleSize}
        toggleAllInStandard={toggleAllInStandard}
        handleAutoZoom={handleAutoZoom}
        getStandardColor={getStandardColor}
        compact={compact}
      />
      
      <SizePanel
        sizes={PHOTO_SIZES}
        categoryId="photo"
        categoryName="Photo Print Sizes"
        tooltip="Common photograph print dimensions used by photo labs and services."
        isExpanded={panelStates.photo}
        togglePanel={togglePanel}
        selectedSizes={selectedSizes}
        toggleSize={toggleSize}
        toggleAllInStandard={toggleAllInStandard}
        handleAutoZoom={handleAutoZoom}
        getStandardColor={getStandardColor}
        compact={compact}
      />
      
      {/* Reference Objects Panel */}
      <ReferenceObjectsPanel
        objects={COMMON_REFERENCE_OBJECTS}
        isExpanded={panelStates.refobj}
        togglePanel={togglePanel}
        selectedObjects={selectedReferenceObjects}
        toggleObject={toggleReferenceObject}
        toggleAllObjects={toggleAllReferenceObjects}
        handleAutoZoom={handleAutoZoom}
        compact={compact}
      />
      
      {/* Removed legend as it's now represented by the colored headers */}
    </div>
  );
};